import type { ReactNode } from 'react'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { Link, useLocation } from 'react-router-dom'

export interface TabItem {
  label: string
  path: string
  disabled?: boolean
  id?: string
  isActive?: (currentPath: string) => boolean
  /**
   * In-page tab: selecting it flips local state instead of navigating. When
   * set the tab renders as a button and `isActive` decides its state.
   */
  onSelect?: () => void
  count?: number
  badge?: 'beta' | 'coming-soon' | 'new'
  icon?: ReactNode
  prominent?: boolean
}

interface PageTabsProps {
  tabs: TabItem[]
  className?: string
}

/** Edge fade applied to whichever side of the strip has tabs scrolled out of view. */
const FADE_MASKS = {
  none: undefined,
  start:
    'linear-gradient(to right, transparent 0, hsl(var(--foreground)) 2rem, hsl(var(--foreground)) 100%)',
  end: 'linear-gradient(to right, hsl(var(--foreground)) 0, hsl(var(--foreground)) calc(100% - 2rem), transparent 100%)',
  both: 'linear-gradient(to right, transparent 0, hsl(var(--foreground)) 2rem, hsl(var(--foreground)) calc(100% - 2rem), transparent 100%)',
} as const

type FadeEdge = keyof typeof FADE_MASKS

export function PageTabs({ tabs, className }: PageTabsProps) {
  const location = useLocation()
  const currentPath = location.pathname
  const scrollerRef = useRef<HTMLDivElement>(null)
  const [fadeEdge, setFadeEdge] = useState<FadeEdge>('none')

  // On narrow screens the strip scrolls sideways; fade the edge(s) that hide
  // tabs so a cut-off label reads as "swipe for more", not as a clipped layout.
  useLayoutEffect(() => {
    const scroller = scrollerRef.current
    if (!scroller) return
    const update = () => {
      const maxScroll = scroller.scrollWidth - scroller.clientWidth
      const hasStart = scroller.scrollLeft > 1
      const hasEnd = scroller.scrollLeft < maxScroll - 1
      setFadeEdge(
        hasStart && hasEnd
          ? 'both'
          : hasStart
            ? 'start'
            : hasEnd
              ? 'end'
              : 'none',
      )
    }
    update()
    scroller.addEventListener('scroll', update, { passive: true })
    const observer = new ResizeObserver(update)
    observer.observe(scroller)
    return () => {
      scroller.removeEventListener('scroll', update)
      observer.disconnect()
    }
  }, [tabs.length])

  // A tab's own matcher wins; otherwise fall back to simple path matching.
  const isTabActive = (tab: TabItem) =>
    tab.isActive
      ? tab.isActive(currentPath)
      : currentPath === tab.path || currentPath.endsWith(tab.path)
  const activeIndex = tabs.findIndex(isTabActive)

  // Keep the active tab on screen — deep links and in-page switches can land
  // on a tab that sits past the right edge of a phone-width strip. Scrolls
  // only the strip itself, never the page.
  useEffect(() => {
    const scroller = scrollerRef.current
    const active = scroller?.querySelector<HTMLElement>('[data-active="true"]')
    if (!scroller || !active) return
    const inset = 24
    const start = active.offsetLeft
    const end = start + active.offsetWidth
    if (start - inset < scroller.scrollLeft) {
      scroller.scrollLeft = Math.max(0, start - inset)
    } else if (end + inset > scroller.scrollLeft + scroller.clientWidth) {
      scroller.scrollLeft = end + inset - scroller.clientWidth
    }
  }, [activeIndex, tabs.length])

  const fadeMask = FADE_MASKS[fadeEdge]

  return (
    <div
      ref={scrollerRef}
      className={cn(
        'scrollbar-thin scrollbar-thumb-border relative flex w-full min-w-0 max-w-full space-x-1 overflow-x-auto overflow-y-hidden sm:space-x-4',
        'overscroll-x-contain [-webkit-overflow-scrolling:touch]',
        // Phones swipe the strip; the scrollbar only steals height there.
        'max-sm:[scrollbar-width:none] max-sm:[&::-webkit-scrollbar]:hidden',
        className,
      )}
      style={
        fadeMask
          ? { maskImage: fadeMask, WebkitMaskImage: fadeMask }
          : undefined
      }
    >
      {tabs.map(tab => {
        const isActive = isTabActive(tab)

        // Regular tab without dropdown. Tabs that carry an `onSelect`
        // switch in-page state (e.g. My Work's work-type lens) and render as
        // a button; the rest keep the Link + path behaviour.
        const tabClassName = cn(
          'text-consistent shrink-0 whitespace-nowrap px-2 py-3 text-sm font-medium transition-colors sm:px-4',
          'flex items-center gap-2 border-b-2',
          isActive
            ? 'border-primary text-foreground'
            : 'border-transparent text-muted-foreground hover:border-muted-foreground/30 hover:text-foreground',
          tab.disabled && 'pointer-events-none cursor-not-allowed opacity-50',
          tab.prominent &&
            'rounded-t-lg bg-primary/5 font-semibold text-primary',
          tab.prominent && isActive && 'border-primary bg-primary/10',
        )

        const tabContent = (
          <>
            {tab.icon}
            <span>{tab.label}</span>
            {tab.badge && (
              <Badge
                variant={
                  tab.badge === 'beta'
                    ? 'secondary'
                    : tab.badge === 'new'
                      ? 'success'
                      : 'outline'
                }
                className={cn(
                  'h-5 px-1.5 text-[10px] font-medium uppercase tracking-wide',
                  tab.badge === 'beta' &&
                    'border-amber-500/30 bg-amber-500/15 text-amber-600 dark:text-amber-400',
                  tab.badge === 'coming-soon' &&
                    'bg-muted text-muted-foreground',
                  tab.badge === 'new' &&
                    'border-emerald-500/30 bg-emerald-500/15 text-emerald-600 dark:text-emerald-400',
                )}
              >
                {tab.badge === 'coming-soon' ? 'Soon' : tab.badge}
              </Badge>
            )}
            {typeof tab.count === 'number' && tab.count > 0 && (
              <Badge
                variant="secondary"
                className="h-5 border-0 bg-muted/60 px-1.5 text-xs font-normal text-muted-foreground"
              >
                {tab.count.toLocaleString()}
              </Badge>
            )}
          </>
        )

        if (tab.onSelect) {
          return (
            <button
              key={tab.id || tab.path}
              type="button"
              role="tab"
              aria-selected={isActive}
              data-active={isActive || undefined}
              className={tabClassName}
              disabled={tab.disabled}
              onClick={tab.onSelect}
            >
              {tabContent}
            </button>
          )
        }

        return (
          <Link
            key={tab.path}
            to={tab.disabled ? '#' : tab.path}
            aria-current={isActive ? 'page' : undefined}
            data-active={isActive || undefined}
            className={tabClassName}
            onClick={e => {
              if (tab.disabled) {
                e.preventDefault()
              }
            }}
          >
            {tabContent}
          </Link>
        )
      })}
    </div>
  )
}
